// Guest-side request tracking for the in-room QR page: received → seen & on the way → done (polls the public status endpoint).
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Footprints, Inbox, XCircle } from 'lucide-react';
import { requestStatusText } from '../../pages/inroom_i18n';

const API = process.env.REACT_APP_BACKEND_URL || '';
const KEY = 'lrn_inroom_requests';
const KEEP_MS = 3 * 3600 * 1000;

export const rememberRequest = (id) => {
  try {
    const now = Date.now();
    const list = (JSON.parse(localStorage.getItem(KEY) || '[]')).filter((o) => now - o.at < KEEP_MS && o.id !== id);
    list.unshift({ id, at: now });
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 5)));
  } catch { /* storage blocked */ }
};

export const recentRequestIds = () => {
  try {
    const now = Date.now();
    return (JSON.parse(localStorage.getItem(KEY) || '[]')).filter((o) => now - o.at < KEEP_MS).map((o) => o.id);
  } catch { return []; }
};

const STEPS = ['new', 'accepted', 'done'];
const ICON = { new: Inbox, accepted: Footprints, done: CheckCircle2, dismissed: XCircle };

const useRequestStatus = (id) => {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let stop = false;
    let timer = null;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/inroom/request/${id}/status`);
        if (res.ok) { const j = await res.json(); if (!stop) setSt(j); if (j.status === 'done' || j.status === 'dismissed') return; }
      } catch { /* retry */ }
      if (!stop) timer = setTimeout(tick, 10000);
    };
    timer = setTimeout(tick, 0);
    return () => { stop = true; clearTimeout(timer); };
  }, [id]);
  return st;
};

export const RequestTracker = ({ requestId, lang = 'en', compact = false }) => {
  const st = useRequestStatus(requestId);
  const status = st?.status || 'new';
  const idx = STEPS.indexOf(status);
  const Icon = ICON[status] || Inbox;
  const closed = status === 'dismissed';
  const steps = STEPS.map((s, i) => (
    <span key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${closed ? 'bg-slate-300' : i <= idx ? 'bg-sky-500' : 'bg-slate-200'}`} />
  ));
  const when = status === 'accepted' ? st?.accepted_at : (status === 'done' ? st?.handled_at : null);
  return (
    <div className={`rounded-xl border ${closed ? 'border-slate-200 bg-slate-50' : 'border-sky-200 bg-sky-50'} ${compact ? 'p-3' : 'p-4'} text-left`} data-testid={`request-tracker-${requestId}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${closed ? 'text-slate-400' : status === 'done' ? 'text-sky-700' : 'text-sky-600 animate-pulse'}`} />
        <p className="text-sm font-semibold text-slate-900" data-testid={`request-status-${requestId}`}>{requestStatusText(lang, status, st?.accepted_by)}</p>
      </div>
      <div className="flex gap-1 mt-2">{steps}</div>
      <p className="text-[11px] text-slate-500 mt-1.5">
        {compact && st?.type ? `${String(st.type).replace(/_/g, ' ')} · ` : ''}{status === 'new' ? requestStatusText(lang, 'wait') : ''}{when ? ` ${String(when).slice(11, 16)}` : ''}
      </p>
    </div>
  );
};

export const RecentRequests = ({ lang, excludeId }) => {
  const ids = recentRequestIds().filter((id) => id !== excludeId);
  if (!ids.length) return null;
  const trackers = ids.map((id) => <RequestTracker key={id} requestId={id} lang={lang} compact />);
  return (
    <div className="mb-4 space-y-2" data-testid="inroom-recent-requests">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{requestStatusText(lang, 'title')}</p>
      {trackers}
    </div>
  );
};
